/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import { hasRole } from 'linced-app/utils/roles';
import config from 'linced-app/config/environment';
import { saveAs } from 'file-saver';
import moment from 'moment';
import {
  downloadFromBytes,
  getNzMoeRollReturnFileName,
} from 'linced-app/utils/files';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';

export default class IndexController extends Controller {
  // Services
  @service
  fetch;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @alias('session.school.jurisdictionalId')
  jurisdictionId;

  // State
  // rollReturnCodeSelected contains the selected roll return month code for
  // limiting the loading spinner to a single button.
  @tracked rollReturnCodeSelected = '';

  // rollReturnCreating provides the mutable state for if a roll return is
  // being created.
  @tracked rollReturnCreating = false;

  // tableSelected contains the selected table id limiting the loading
  // spinner to a single button.
  // Note:
  //  - If multiple table download buttons are smashed in quick succession,
  //    only the last one clicked will be disabled while downloading.
  @tracked tableSelected = '';

  // tableDownloading provides the mutable state to stop a given button if a
  // roll return table is being downloaded.
  @tracked tableDownloading = false;

  @tracked listSelected = '';

  @tracked listDownloading = false;

  // Methods
  @hasRole(Staff.APPROVE_ROLL_RETURNS)
  canApprove;

  // pendingApproval returns the most recently created roll return which is currently pending approval.
  @computed('model.returns.@each.{status,createTime}')
  get pendingApproval() {
    let pending = this.model.returns.filter(
      (item) => item.status === 'Pending approval',
    );
    return pending.slice().sort((a, b) => a.createTime - b.createTime)[0];
  }
  // pendingUpload returns the most recently created roll return which is currently pending upload.
  @computed('model.returns.@each.{status,createTime}')
  get pendingUpload() {
    let pending = this.model.returns.filter(
      (item) => item.status === 'Approved (pending upload)',
    );
    return pending.slice().sort((a, b) => a.createTime - b.createTime)[0];
  }

  @tracked
  validateOnly = false;

  @action
  setValidateOnly(value) {
    this.validateOnly = value;
  }

  @computed('model.dates.[]')
  get draftMode() {
    let now = moment();
    return this.model.dates.every((returnDate) => {
      let closeDate = moment(returnDate.officialClose, 'YYYY-MM-DD');
      return now.isAfter(closeDate, 'd');
    });
  }

  @tracked deleteInProgress = false;

  @tracked selectedRollReturn = null;

  @tracked deleteDialogOpen = false;

  @tracked deleteDialogLabel = 'Roll Return';

  // reset pushes controller properties back to defaults.
  // It is called by the Route.resetController hook either when the model
  // changes or the route is exiting.
  reset() {
    this.rollReturnCodeSelected = '';
    this.rollReturnCreating = false;
    this.tableSelected = '';
    this.tableDownloading = false;
  }

  @action
  createRollReturn(month, year) {
    if (this.rollReturnCreating) {
      // only allow one request in flight at a time.
      return;
    }

    this.rollReturnCodeSelected = month;
    this.set(`rollReturnCreating`, true);

    let rollReturn = this.store.createRecord('nz-roll-returns/roll-return', {
      rollReturnMonth: month,
      rollReturnYear: year,
      validateOnly: this.validateOnly,
    });

    rollReturn
      .save()
      .then((rr) => {
        this.router.transitionTo('nz-moe.roll-returns.roll-return', rr.id);
      })
      .catch(() => {
        this.rollReturnCodeSelected = '';
        this.set(`rollReturnCreating`, false);
      });

    // Note:
    // - This relies on reset controller to reset values as `Router.transitionTo`
    //   is slow to load data and the promise chain resolves before the
    //   transition has completed. As such, the loading spinner can clear
    //   about 2 seconds before the transition actually completes the paint
    //   phase.
  }

  @action
  downloadMOEFile(rollReturn) {
    let fileName = getNzMoeRollReturnFileName(
      this.jurisdictionId,
      rollReturn,
      '',
      'moe',
    );
    saveAs(
      new Blob([rollReturn.moeFileData], {
        type: 'data:text/csv;charset=utf-8',
      }),
      fileName,
    );
  }

  @action
  viewTables(rollReturn) {
    if (this.tableDownloading) {
      // only allow one request in flight at a time.
      return;
    }

    this.tableSelected = rollReturn.id;
    this.tableDownloading = true;
    this.fetch
      .request(
        `${config.APP.apiHost}/providers/nz/moe/v4/rollreturns/rollreturns/${rollReturn.id}/:tables`,
      )
      .then((resp) => {
        let printer = window.open(
          'about:blank',
          '_blank',
          'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=800,width=1280',
        );
        printer.document.body.innerHTML = resp.tableData;
      })
      .finally(() => {
        this.tableSelected = '';
        this.tableDownloading = false;
      });
  }

  @action
  deleteRollReturn(rollReturn, label) {
    this.selectedRollReturn = rollReturn;
    this.deleteDialogOpen = true;
    this.deleteDialogLabel = label;
  }

  @action
  confirmDelete(confirmDelete) {
    if (confirmDelete) {
      this.deleteInProgress = true;
      this.selectedRollReturn
        .destroyRecord()
        .then(() => {
          this.toaster.success(
            `${this.deleteDialogLabel} deleted successfully.`,
          );
        })
        .catch(() => {
          this.selectedRollReturn.rollbackAttributes();
          this.toaster.error(`Error deleting ${this.deleteDialogLabel}.`);
        })
        .finally(() => {
          this.deleteInProgress = false;
          this.deleteDialogOpen = false;
          this.selectedRollReturn = null;
        });
    } else {
      this.deleteInProgress = false;
      this.deleteDialogOpen = false;
      this.selectedRollReturn = null;
    }
  }

  @action
  downloadStudentLists(rollReturn) {
    if (this.listDownloading) {
      // only allow one request in flight at a time.
      return;
    }

    this.listSelected = rollReturn.id;
    this.listDownloading = true;
    this.fetch
      .request(
        `${config.APP.apiHost}/providers/nz/moe/v4/rollreturns/rollreturns/${rollReturn.id}/:lists`,
      )
      .then((resp) => {
        if (isEmpty(resp) || isEmpty(resp.byteData)) {
          this.toaster.error(`Error downloading student lists!`);
        } else {
          let fileName = getNzMoeRollReturnFileName(
            this.jurisdictionId,
            rollReturn,
            ' - Student Lists',
            'zip',
          );
          downloadFromBytes(resp.byteData, 'application/zip', fileName);
        }
      })
      .catch(() => {
        this.toaster.error(`Error downloading student lists!`);
      })
      .finally(() => {
        this.listSelected = '';
        this.listDownloading = false;
      });
  }
}
